import {WorkflowPageStatusContext} from './WorkflowPageStatusContext';
import {workflowViewedSources,startWorkflowPageReads,workflowInitialStep,startWorkflowStepReads,WorkflowInitialReads} from './workflowPageLoadPolicy';
import {resetWorkflowHandoff} from '../../services/api/workflowReadHandoff';
import {scriptQualityContinuation} from './workflowScriptQuality';
import {anyQualityBusy} from './workflowQualityWaitPolicy';
import {createWorkflowLoader,workflowRunChangeToken} from './workflowRefreshCoordinator';
// 261010 Epic 2 / P2a + P2b-1 + P2b-2 + P2b-3 + P2c-2 (hàng "Máy tự chạy" dưới thanh 9 bước ở cả hai tầng) — khung mới "bảng 9 bước" cho plan Sky (P2a: chỉ đọc; P2b-1: bước Sơ đồ / Storyboard / Prompt có thao tác; P2b-2: thêm Video và Ghép; P2b-3: Kịch bản và Đạo diễn sửa tay — theo workflowActionPolicy): tầng 1 bảng cấp tập, tầng 2 chi tiết video (`?event=<id>&step=<KEY>`).
// Bảng chỉ cần `workflow-status` (đã mang tên cảnh · thời lượng · số shot · số thoại của từng video); chi tiết plan/storyboard/sơ đồ nạp LƯỜI khi mở một video (lượt 2: `/events` nặng 3–6 s).
// Mẫu: project-docs/261010-hoc-luong-sky/_p1/mockup-chot. Thao tác ghi ở bảy bước Kịch bản / Sơ đồ / Storyboard / Prompt / Đạo diễn / Video / Ghép (hộp xác nhận KHÔNG giá; Kịch bản và Đạo diễn chỉ SỬA TAY, không chạy AI); hai bước còn lại (Tài sản, Quay phim) vẫn làm ở UI cũ (nút "Mở UI cũ"). Đang có nháp chưa lưu ⇒ đổi bước / video / quay về bảng đều hỏi "Bỏ thay đổi chưa lưu?".
// Logic lọc/nhóm/nhãn/đường dẫn nằm ở workflowBoardUtils.ts / workflowDetailUtils.ts (thuần, có test).
// 261010 UI1a — khung 8 bước: nút chính / nút đầu bước nối route L1 qua `useWorkflowStepActions` (KHÔNG gọi `script/approve` / `workflow/run` cũ; Dừng KHÔNG dùng `releaseFilmJob`). Bước đang xem nạp chi tiết plan MỘT lần (QĐ 7).
// 261010 P8b-1 — container: state / load / poll / guard / handler ở lại đây; JSX dời sang WorkflowTopBar / WorkflowBoard / WorkflowEventDetail / WorkflowPageStates (move-only).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Modal, message } from 'antd';
import { filmApi } from '../../services/api/film';
import { useAliveRef } from '../../hooks/useAliveRef';
import type { EventPlan, FilmEvent, FilmRunningJob, PositionMapsResponse, StoryboardListItem } from '../../types/film';
import type { WorkflowStatus, WorkflowStepKey } from '../../types/filmWorkflow';
import type { PromptPick } from '../../types/filmStepReview';
import { apiErrorText } from './apiErrorText';
import {
  BoardFilter, isMergedStep, legacyPlanPath, parseWorkflowQuery, plansListPath, workflowPath,
} from './workflowBoardUtils';
import { useStepReviews } from './useStepReviews';
import { jobsFinished } from './workflowActionPolicy';
import { addDismissedJobs, loadDismissedJobs } from './dismissedJobsStore';
import { planDisplayName } from './workflowHeroUtils';
import type { BellNav } from './workflowBellPolicy';
import { boardStepOf, withBudgetNotices } from './workflowPrimaryPolicy';
import { useWorkflowStepActions } from './useWorkflowStepActions';
import { loadCapCutRows } from './workflowCapCutUtils';
import { downloadAll } from './capCutDownload';
import WorkflowTopBar from './WorkflowTopBar';
import WorkflowPlanTools from './WorkflowPlanTools'; // 261010 P9D D9
import WorkflowBoard from './WorkflowBoard';
import WorkflowEventDetail from './WorkflowEventDetail';
import { WorkflowEmptyPlan, WorkflowEventMissing, WorkflowLoadError, WorkflowLoading } from './WorkflowPageStates';
import './workflowBoard.css';
import './workflowBoardCards.css';
import { startVisiblePoll } from './workflowVisiblePoll';

const REFRESH_MS = 8000; // chỉ poll nhẹ khi plan đang có việc nền (P2b-1: các thao tác đã có việc nền nên cần thấy kết thúc sớm hơn)

const FilmWorkflowPage: React.FC = () => {
  const { storyId = '', planId = '' } = useParams<{ storyId: string; planId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId: eventParam, step: stepParam } = useMemo(() => parseWorkflowQuery(location.search), [location.search]);
  // U32b — `?step=PROMPT|VIDEO` (liên kết cũ gồm cả `?event=`) mở ô gộp «Prompt & Video» trên BẢNG, `?event=` chỉ là video đang chọn; các bước khác + `?event=` vẫn là trang chi tiết một video.
  const mergedRoute = isMergedStep(stepParam);
  const eventId = mergedRoute ? null : eventParam; // eventId = video của TRANG CHI TIẾT
  const mergedEventId = mergedRoute ? eventParam : null;

  const [status, setStatus] = useState<WorkflowStatus | null>(null);
  const [detail, setDetail] = useState<EventPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [storyboards, setStoryboards] = useState<StoryboardListItem[] | null>(null);
  const [storyboardsError, setStoryboardsError] = useState<string | null>(null);
  const [maps, setMaps] = useState<PositionMapsResponse | null>(null);
  const [mapsError, setMapsError] = useState<string | null>(null);
  const [filter, setFilter] = useState<BoardFilter>({ step: null, state: 'AUTO' }); // sống ở cha ⇒ không mất khi mở chi tiết rồi quay lại
  const boardFilter = useMemo<BoardFilter>(() => (filter.step || !mergedRoute ? filter : { ...filter, step: 'VIDEO' }), [filter, mergedRoute]); // liên kết cũ ?step=PROMPT|VIDEO ⇒ ô gộp
  const [historyOpen, setHistoryOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false); // 261010 P9D D9 — drawer «Công cụ plan»
  const [bellOpen, setBellOpen] = useState(false); // 261010 P2c-2: điều khiển được để liên kết "Xem N thẻ gắn cờ" mở chuông
  const [dismissedJobIds, setDismissedJobIds] = useState<string[]>(() => loadDismissedJobs());
  const [bellError, setBellError] = useState<string | null>(null);
  const [busyClipId, setBusyClipId] = useState<string | null>(null); // 261010 UI1b: bản clip đang được chọn từ chồng bản
  const [promptPick, setPromptPick] = useState<PromptPick | null>(null); // 261010 P5d: câu AI trích (phiếu AI chấm prompt) + các bản chữ của phiếu, đang được đánh dấu trong khung prompt của bước Prompt
  useEffect(() => { setPromptPick(null); }, [eventId, stepParam]);
  const [clipFocus, setClipFocus] = useState<string | null>(null); // 261010 P6: bản clip người xem chọn xem phiếu (null ⇒ mặc định: bản đang chọn / bản DONE mới nhất)
  useEffect(() => { setClipFocus(null); }, [eventId]);
  const boardScroll = useRef(0);
  const alive = useAliveRef();
  // Kết quả của lượt gọi cho plan CŨ (đổi plan giữa chừng) không được ghi đè plan mới.
  const planRef = useRef(planId);
  planRef.current = planId;
  const statusRef = useRef<WorkflowStatus | null>(status); // 261010 P8a: `baseHash` của duyệt kịch bản = hash MỚI NHẤT BE đã trả (không dùng bản đóng băng trong closure)
  statusRef.current = status;
  // Nguồn nạp lười của tầng 2 (chi tiết plan, storyboard, sơ đồ): mỗi nguồn chỉ hỏi MỘT lần cho mỗi plan — dùng ref để re-render không bắn lặp.
  const asked = useRef({ detail: false, storyboards: false, maps: false });
  const [initialReads,setInitialReads]=useState<WorkflowInitialReads|null>(null);
  // 261029 P3 F1: đổi plan / rời trang ⇒ bỏ mọi lượt đọc sớm đang giữ (cleanup chạy TRƯỚC effect nạp của plan mới).
  useEffect(()=>()=>resetWorkflowHandoff(),[planId]);
  useEffect(()=>{
    const requested=eventId?(stepParam??'SCRIPT'):(filter.step??stepParam);
    // Chưa có status: đoán bước đầu như cũ (route / heuristic từ events). Có status: CHỈ khi bước đang hiện là ASSETS mà chưa đọc sớm (heuristic trượt) mới bắn — bước khác không đọc sớm.
    const step=status?(boardStepOf(status,requested)==='ASSETS'?'ASSETS' as WorkflowStepKey:null):workflowInitialStep(detail?.plan.id===planId?detail:null,requested);
    if(!step||initialReads?.planId===planId&&initialReads.step===step)return;
    setInitialReads(startWorkflowStepReads(planId,step,filmApi,{storyId,api:filmApi as any}));
  },[planId,storyId,eventId,stepParam,filter.step,detail,status,initialReads]);

  const dirtyRef=useRef(false),pendingDraftRefresh=useRef(false);
  const loaders=useRef({status:createWorkflowLoader<WorkflowStatus>(),detail:createWorkflowLoader<EventPlan>(),boards:createWorkflowLoader<StoryboardListItem[]>(),maps:createWorkflowLoader<PositionMapsResponse>()});
  /** Chỉ trạng thái 9 bước — đủ để vẽ bảng. `getEventPlan` (nặng, 3–6 s) KHÔNG nạp ở đây mà nạp lười khi mở chi tiết. */
  const load = useCallback(async (silent = false) => {
    if (!planId) return;
    if (!silent) { setLoading(true); setError(null); }
    await loaders.current.status.run(planId,()=>filmApi.getWorkflowStatus(planId),st=>{statusRef.current=withBudgetNotices(st);setStatus(statusRef.current);setError(null);},e=>{setError(e);},()=>alive.current&&planRef.current===planId);
    if(alive.current&&planRef.current===planId)setLoading(false);
  }, [planId, alive]);

  const loadDetail = useCallback(async () => {
    if (!planId) return;
    asked.current.detail = true;
    if(dirtyRef.current){pendingDraftRefresh.current=true;message.info('Có kết quả mới; lưu hoặc huỷ nháp để tải lại.');return;}
    setDetailLoading(true);setDetailError(null);
    await loaders.current.detail.run(planId,()=>filmApi.getEventPlan(planId),value=>{if(dirtyRef.current)pendingDraftRefresh.current=true;else setDetail(value);},e=>setDetailError(apiErrorText(e,'Tải chi tiết plan')),()=>alive.current&&planRef.current===planId);
    if(alive.current&&planRef.current===planId)setDetailLoading(false);
  }, [planId, alive]);

  useEffect(() => {
    Object.values(loaders.current).forEach(l=>l.invalidate());
    asked.current = { detail: false, storyboards: false, maps: false };
    setStatus(null); setDetail(null); setDetailError(null); setStoryboards(null); setStoryboardsError(null); setMaps(null); setMapsError(null);
    startWorkflowPageReads(()=>load(),()=>loadDetail());
  }, [load,loadDetail]);

  // việc nền đang chạy ⇒ làm tươi nhẹ trạng thái, không bật spinner (chi tiết đã nạp thì làm tươi theo, im lặng)
  // P2b-2: clip PENDING mà không có job (vd. server khởi động lại giữa chừng) cũng cần làm tươi — bước Video báo RUNNING từ chính clip PENDING.
  const hasJobs = (status?.jobs.length ?? 0) > 0 || !!status?.events.some((e) => e.steps.VIDEO === 'RUNNING') || Object.values(status?.stepRuns ?? {}).some((r) => r?.canStop === true) || anyQualityBusy(status); // UI1a: lượt chạy Tài sản đang chạy (Dừng được) cũng cần poll · P9Q: AI đang chấm/sửa bước nào thì còn poll
  useEffect(() => {
    if (!hasJobs) return undefined;
    // 261029 P1 A4: dừng khi tab ẩn; tab hiện lại ⇒ một tick đầy đủ (jobs rồi status) ngay; một lượt >8 s thì không gọi chồng (xem workflowVisiblePoll).
    return startVisiblePoll(() => filmApi.getPlanJobs(planId).catch(()=>undefined).then(()=>load(true)), REFRESH_MS);
  }, [hasJobs, load,planId]);

  const loadStoryboards = useCallback(() => {
    asked.current.storyboards = true; setStoryboardsError(null); // KHÔNG xoá dữ liệu đang có: tải lại không được làm nháp khung / khối bước nháy về "đang tải"
    if(dirtyRef.current){pendingDraftRefresh.current=true;return;}
    return loaders.current.boards.run(planId,()=>filmApi.getStoryboards(planId),value=>{if(dirtyRef.current)pendingDraftRefresh.current=true;else setStoryboards(value);},e=>setStoryboardsError(apiErrorText(e,'Tải storyboard')),()=>alive.current&&planRef.current===planId);
  }, [planId, alive]);
  const loadMaps = useCallback(() => {
    asked.current.maps = true; setMapsError(null);
    if(dirtyRef.current){pendingDraftRefresh.current=true;return;}
    return loaders.current.maps.run(planId,()=>filmApi.getPositionMaps(planId),value=>{if(dirtyRef.current)pendingDraftRefresh.current=true;else setMaps(value);},e=>setMapsError(apiErrorText(e,'Tải sơ đồ')),()=>alive.current&&planRef.current===planId);
  }, [planId, alive]);

  // Tầng 2: nạp lười, MỘT lần mỗi nguồn, khi lần đầu mở một video (bảng không đụng tới).
  const needAux = !!eventId;
  const viewed=workflowViewedSources(eventId?(stepParam??'SCRIPT'):status?boardStepOf(status,boardFilter.step):boardFilter.step);
  useEffect(() => {
    if (!needAux) return;
    if (!asked.current.detail) loadDetail();
    if (viewed.boards && !asked.current.storyboards) loadStoryboards();
    if (viewed.maps && !asked.current.maps) loadMaps();
  }, [needAux, viewed.boards,viewed.maps,loadDetail, loadStoryboards, loadMaps]);

  // 261010 UI1a (QĐ 7): màn bảng luôn có một bước đang chọn ⇒ nạp chi tiết plan MỘT lần (view đã có cần `ev`; Tài sản cần `ev` đầu + `refBank`); sơ đồ / storyboard chỉ khi bước đó cần.
  const boardStep = status && !eventId ? boardStepOf(status, boardFilter.step) : null;
  useEffect(() => {
    if (!boardStep) return;
    if (!asked.current.detail) loadDetail();
    if (viewed.boards && !asked.current.storyboards) loadStoryboards();
    if (viewed.maps && !asked.current.maps) loadMaps();
  }, [boardStep,viewed.boards,viewed.maps, loadDetail, loadStoryboards, loadMaps]);

  /** Tải lại tay: trạng thái luôn; chi tiết/storyboard/sơ đồ chỉ khi đã từng nạp (bảng chưa cần thì không tốn). */
  const reloadAll = useCallback(() => {
    resetWorkflowHandoff();   // 261029 P3: làm mới tay ⇒ không còn kết quả đọc sớm nào được giao
    load(true);
    if (asked.current.detail) loadDetail();
    if (asked.current.storyboards) loadStoryboards();
    if (asked.current.maps) loadMaps();
  }, [load, loadDetail, loadStoryboards, loadMaps]);

  /** Hỏi `workflow-status` NGAY (sau 202 của việc nền) và trả jobs/failures mới cho widget. */
  const refreshStatus = useCallback(async (): Promise<{ jobs: FilmRunningJob[]; failures: WorkflowStatus['failures'] } | undefined> => {
    await load(true);const st=statusRef.current;if(!st||planRef.current!==planId)return undefined;return {jobs:st.jobs,failures:st.failures};
  }, [planId, load]);

  /**
   * Sau MỌI thao tác ghi (thành công hay lỗi): tải lại CẢ dữ liệu chi tiết của widget (chi tiết plan / storyboard / sơ đồ — chỉ những nguồn đã từng nạp)
   * LẪN `workflow-status`. Không suy trạng thái từ tên thao tác.
   */
  const onActionDone = useCallback(() => {
    load(true);
    if (asked.current.detail) loadDetail();
    if (asked.current.storyboards) loadStoryboards();
    if (asked.current.maps) loadMaps();
  }, [load, loadDetail, loadStoryboards, loadMaps]);

  const scriptContinuation=useRef(new Set<string>());
  useEffect(()=>{
    const source=scriptQualityContinuation(status);if(!source||scriptContinuation.current.has(source))return;
    scriptContinuation.current.add(source);
    void (async()=>{try{const preview=await filmApi.previewWorkflowStep(planId,'SCRIPT',{scope:'missing'});if(!alive.current||planRef.current!==planId)return;await filmApi.runWorkflowStep(planId,'SCRIPT',{scope:'missing',contractVersion:1,confirmationHash:preview.confirmationHash,requestId:source,confirm:true});await refreshStatus();}catch(e){if(alive.current&&planRef.current===planId)message.warning(apiErrorText(e,'Chấm Phân đoạn — tải lại phiếu để xử lý'));}})();
  },[status,planId,alive,refreshStatus]);

  // 261010 P6 — phiếu "AI chấm clip" của video đang mở ở bước Video: nạp + poll ở ĐÂY (một chủ), thẻ trên đầu và các dòng bản clip chỉ đọc/gọi qua nó. Bước khác ⇒ không nạp.
  const clipCtl = useStepReviews({ planId, eventId: eventId ?? '', onActionDone, enabled: !!eventId && stepParam === 'VIDEO' });

  // Việc nền vừa XONG (có ở lượt poll trước, mất ở lượt này) ⇒ nạp lại dữ liệu chi tiết: nếu không, dải chấm báo xong mà khung / sơ đồ vẫn là bản cũ tới khi F5.
  const prevJobs = useRef<FilmRunningJob[] | null>(null);
  const runChange=useRef('');
  useEffect(() => {
    if (!status) return;
    const token=workflowRunChangeToken(status.stepRuns);
    if (jobsFinished(prevJobs.current, status.jobs).length||runChange.current&&token!==runChange.current) {
      if (asked.current.detail) loadDetail();
      if (asked.current.storyboards) loadStoryboards();
      if (asked.current.maps) loadMaps();
    }
    prevJobs.current = status.jobs;runChange.current=token;
  }, [status, loadDetail, loadStoryboards, loadMaps]);

  // 261010 P2b-3 — nháp sửa tay chưa lưu (bước Kịch bản / Đạo diễn báo lên qua `setEditDirty`): mọi lối RỜI trang trong app đi qua `guard` ⇒ hỏi "Bỏ thay đổi chưa lưu?". Không lưu tự động.
  // Giới hạn đã biết: nút Back / Forward của TRÌNH DUYỆT không chặn được dưới BrowserRouter (không có useBlocker); đóng tab / tải lại thì có `beforeunload`.
  const setEditDirty = useCallback((d: boolean) => { dirtyRef.current = d;if(!d&&pendingDraftRefresh.current){pendingDraftRefresh.current=false;onActionDone();} }, [onActionDone]);
  const guard = useCallback((go: () => void) => {
    if (!dirtyRef.current) { go(); return; }
    Modal.confirm({
      title: 'Bỏ thay đổi chưa lưu?', content: 'Bản sửa tay chưa lưu của video này sẽ mất.', okText: 'Bỏ thay đổi', cancelText: 'Ở lại', okButtonProps: { danger: true },
      onOk: () => { dirtyRef.current = false; go(); },
    });
  }, []);

  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);

  const openLegacy = useCallback(() => guard(() => navigate(legacyPlanPath(storyId, planId))), [guard, navigate, storyId, planId]);
  const openPlans = useCallback(() => guard(() => navigate(plansListPath(storyId))), [guard, navigate, storyId]);
  const openEvent = useCallback((id: string, step?: WorkflowStepKey | null) => guard(() => {
    boardScroll.current = window.scrollY;
    setHistoryOpen(false);
    navigate(workflowPath(storyId, planId, { eventId: id, step: step ?? stepParam ?? filter.step ?? 'SCRIPT' }));
  }), [guard, navigate, storyId, planId, stepParam, filter.step]);
  const backToBoard = useCallback(() => guard(() => {
    setHistoryOpen(false);
    navigate(workflowPath(storyId, planId));
  }), [guard, navigate, storyId, planId]);
  // về bảng: khôi phục vị trí cuộn của tầng 1
  // (chỉ theo `eventId`: nếu phụ thuộc `status` thì mỗi lượt làm tươi 15 giây sẽ kéo người dùng về đầu bảng)
  useEffect(() => { if (!eventId) window.scrollTo(0, boardScroll.current); }, [eventId]);

  const eventsById = useMemo(() => {
    const m = new Map<string, { ev: FilmEvent; sceneId: string }>();
    detail?.scenes.forEach((s) => s.events.forEach((e) => m.set(e.id, { ev: e, sceneId: s.id })));
    return m;
  }, [detail]);
  const planName = planDisplayName(status?.plan, detail?.plan.name, planId);

  // 261010 UI1a — mở một bước trên bảng (nút "Xử lý N lỗi ở bước 2", chuông): chọn bước rồi cuộn tới neo (nếu có) sau khi vẽ.
  const scrollToId = useCallback((id?: string) => { if (id) window.setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120); }, []);
  const openStep = useCallback((step: WorkflowStepKey, anchor?: string) => {
    setHistoryOpen(false);
    setFilter({ step, state: 'AUTO' });
    if (eventId || (mergedRoute && !isMergedStep(step))) navigate(workflowPath(storyId, planId)); // sang bước khác ⇒ bỏ ?event=&step= của ô gộp
    scrollToId(anchor);
  }, [eventId, mergedRoute, navigate, storyId, planId, scrollToId]);
  // U32b — chọn video ở ô gộp: ghi `?event=<id>&step=VIDEO` (thay thế, không chồng lịch sử).
  const selectVideo = useCallback((id: string) => guard(() => navigate(workflowPath(storyId, planId, { eventId: id, step: 'VIDEO' }), { replace: true })), [guard, navigate, storyId, planId]);
  const downloadCapCut = useCallback(() => {
    openStep('VIDEO');
    const st = statusRef.current;
    if (!st) return;
    // Nút chính «Tải clip cho CapCut»: hỏi export TRƯỚC (BE là nguồn); chỉ khi export không có / lỗi mới dùng workflow-status + chi tiết plan.
    void loadCapCutRows({
      enabled: st.stepCapabilities?.VIDEO?.supported === true, fetchExport: () => filmApi.getVideoExport(planId), events: st.events,
      detail: new Map(Array.from(eventsById.entries()).map(([id, v]) => [id, v.ev])),
    }).then((v) => { if (alive.current && planRef.current === planId) downloadAll(v.rows); });
  }, [openStep, eventsById, planId, alive, planRef]);
  const stepActions = useWorkflowStepActions({ planId, statusRef, alive, planRef, guard, refreshStatus, openStep, downloadCapCut, status,initialReads,viewedStep:eventId?(stepParam??'SCRIPT'):boardStep });
  // 261010 UI1b — "Chọn bản này" ở chồng bản Video: route chọn clip hiện có (hộp ghi lý do của phiếu AI chấm chỉ có ở ngăn Chi tiết).
  const selectClip = useCallback((evId: string, clipId: string) => guard(async () => {
    setBusyClipId(clipId);
    try { await filmApi.selectEventClip(evId, clipId); message.success('Đã chọn bản clip'); }
    catch (e) { if (alive.current) message.error(apiErrorText(e, 'Chọn clip')); }
    finally { if (alive.current) setBusyClipId(null); onActionDone(); }
  }), [guard, alive, onActionDone]);

  const onBellAction = useCallback((nav: BellNav) => {
    if (nav.type === 'TOGGLE_SWALLOWED') return;
    if (nav.type === 'DISMISS') { guard(() => setDismissedJobIds(addDismissedJobs([nav.jobId]))); return; }
    if (nav.type === 'RELEASE') {
      guard(async () => {
        setBellError(null);
        try {
          await filmApi.releaseFilmJob(nav.jobId);
          await refreshStatus();
        } catch (e) {
          if (alive.current) setBellError(apiErrorText(e, 'Gỡ khoá'));
        }
      });
      return;
    }
    if (nav.type === 'SCROLL_RUN') {
      guard(() => { setBellOpen(false); openStep('SCRIPT', 'wf-primary'); }); // cả ở trang chi tiết video: về bảng, mở bước 1, cuộn tới nút chính
      return;
    }
    if (nav.type === 'OPEN_BOARD') {
      guard(() => {
        setHistoryOpen(false); setBellOpen(false);
        setFilter({ step: nav.step, state: 'AUTO' });
        navigate(workflowPath(storyId, planId));
        scrollToId(nav.anchor);
      });
      return;
    }
    if (nav.type === 'OPEN_FIRST') {
      const evs = statusRef.current?.events ?? [];
      const id = [...evs].sort((a, b) => a.sceneOrder - b.sceneOrder || a.eventOrder - b.eventOrder)[0]?.eventId;
      if (id) openEvent(id, nav.step);
      return;
    }
    if (nav.type === 'OPEN_EVENT') openEvent(nav.eventId, nav.step);
  }, [guard, refreshStatus, alive, navigate, storyId, planId, openEvent, scrollToId, openStep]);

  const top = (
    <>
    <WorkflowTopBar
      planName={planName} status={status} bellOpen={bellOpen} onBellOpenChange={setBellOpen}
      onOpenPlans={openPlans} onOpenTools={() => setToolsOpen(true)} onReloadAll={reloadAll}
      dismissedJobIds={dismissedJobIds} onBellAction={onBellAction} bellError={bellError}
    />
    <WorkflowPlanTools open={toolsOpen} onClose={() => setToolsOpen(false)} planId={planId} jobs={status?.jobs ?? []}
      onOpenPlans={openPlans} onGoStep={(k) => openStep(k)} onActionDone={onActionDone} currentStep={eventId ? (stepParam ?? 'SCRIPT') : (status ? boardStepOf(status, boardFilter.step) : null)} />
    </>
  );

  if (loading && !status) return <WorkflowLoading top={top} />;
  if (error || !status) return <WorkflowLoadError top={top} error={error} onRetry={() => load()} onOpenLegacy={openLegacy} onOpenPlans={openPlans} />;
  if (status.events.length === 0) return <WorkflowEmptyPlan top={top} onOpenLegacy={openLegacy} />;

  if (eventId) {
    const cur = status.events.find((e) => e.eventId === eventId);
    if (!cur) return <WorkflowEventMissing top={top} onBackToBoard={backToBoard} />;
    return (
      <WorkflowPageStatusContext.Provider value={{planId,status,refresh:async()=>{await load(true);return statusRef.current;}}}><WorkflowEventDetail
        top={top} storyId={storyId} planId={planId} eventId={eventId} stepParam={stepParam} status={status}
        detail={detail} detailError={detailError} detailLoading={detailLoading}
        storyboards={storyboards} storyboardsError={storyboardsError} maps={maps} mapsError={mapsError}
        eventsById={eventsById} historyOpen={historyOpen} setHistoryOpen={setHistoryOpen}
        promptPick={promptPick} setPromptPick={setPromptPick} clipFocus={clipFocus} setClipFocus={setClipFocus}
        clipCtl={clipCtl} guard={guard} navigate={navigate}
        onOpenEvent={openEvent} onBackToBoard={backToBoard} onLoadDetail={loadDetail}
        onActionDone={onActionDone} onJobsRefresh={refreshStatus} setEditDirty={setEditDirty}
      /></WorkflowPageStatusContext.Provider>
    );
  }

  return (
    <WorkflowPageStatusContext.Provider value={{planId,status,initialReads:initialReads?.planId===planId?initialReads:null,refresh:async()=>{await load(true);return statusRef.current;}}}><WorkflowBoard
      top={top} status={status} planName={planName} planId={planId} storyId={storyId} filter={boardFilter} setFilter={setFilter} eventsById={eventsById} selectedEventId={mergedEventId} onSelectEvent={selectVideo} onPickStep={(k) => openStep(k)}
      detail={detail} detailLoading={detailLoading} detailError={detailError} onLoadDetail={loadDetail}
      storyboards={storyboards} storyboardsError={storyboardsError} maps={maps} mapsError={mapsError}
      onOpenEvent={openEvent} onActionDone={onActionDone}
      sending={stepActions.sending} nothingSteps={stepActions.nothingSteps} assetsNothingToRun={stepActions.assetsNothingToRun} blockedUpstream={stepActions.blockedUpstream} assetNames={stepActions.assetNames} error={stepActions.error} onCloseError={stepActions.clearError} onRetry={stepActions.retry}
      onReload={reloadAll} onRun={stepActions.run} busyClipId={busyClipId} onSelectClip={selectClip}
    /></WorkflowPageStatusContext.Provider>
  );
};

export default FilmWorkflowPage;
